import { useCallback, useEffect } from 'react';

import { transactionActions } from '../actions';
import useDispatch from '../hooks/useDispatch';
import useSelector from '../hooks/useSelector';
import { MonthOptions } from '../modules/User/PaymentSelection';
import {
  StripeCoupon,
  V1Coupon,
} from '../py-generated/datalouder/checkout/types';

export const PRICE_CONFIG_SANDBOX = {
  clientId:
    'Adb1GAta5tPMm7tRANRhxRGkiqjlHYpChKBXgUF7Dq4MKaPBux7aoH09FF8gwRXysA9FXLoUOXZusiq5',
  plan6m: 'P-2MX87853HR207483GLZXPYRQ',
  plan1y: 'P-1BT27670R79915530LZXQYWA',
  plan6m_trial: 'P-2L891535PL208112DMAGOFZY',
  plan1y_trial: 'P-2L83195056445583RMAGOF7A',
  stripe_plan1m: 'price_1NI3F1KBiRmOhngH4KkLarOl',
  stripe_plan6m: 'price_1Jll2jKBiRmOhngHEjOVH3GK',
  stripe_plan1y: 'price_1Jll2yKBiRmOhngHK10FoBH6',
};

export const PRICE_CONFIG_DEFAULT = {
  clientId:
    'AYunTP1Mb4fMigmG_u-JRjGbcUdIUHXo12anUaZaXeV2K1C7-S8XfnNluE16BHWBH8DdCIedAlccYdHg',
  plan6m: 'P-1TB14917TV072201BLZXQ4JY',
  plan1y: 'P-4NY71726JT979551PLZXQ4OY',
  plan6m_trial: 'P-63G87995RU859850DMAGOKYI',
  plan1y_trial: 'P-2BE283294Y2635944MAGOK3Y',
  stripe_plan1m: 'price_1LUHDsKBiRmOhngHGStW4CMb',
  stripe_plan6m: 'price_1JyCSgKBiRmOhngHbbPDlDB4',
  stripe_plan1y: 'price_1JyCTJKBiRmOhngHOMBjeCM4',
};

export const PLAN_KOL_V1_PROMO = {
  price1m: 779.99,
  price6m: 3449.99,
  price1y: 6199.99,
  plan6m: 'P-5H478963MD6534733L2EUAOQ',
  plan1y: 'P-5NJ64660S4918621VL2EUBJI',
  plan6m_trial: 'P-65X6020375912814RMAGOK7Q',
  plan1y_trial: 'P-6A862330RD853600SMAGOLDA',
  stripe_plan1m: 'price_1LUjLAKBiRmOhngHACCfa5B4',
  stripe_plan6m: 'price_1JyCTdKBiRmOhngHlOG4IOJc',
  stripe_plan1y: 'price_1KpyqgKBiRmOhngHmFKp2LnP',
};

export const STRIPE_ONE_TIME_PRICES = {
  1: 'price_1NHyRbKBiRmOhngHiUu03COU',
  6: 'price_1NHySNKBiRmOhngH0E5iOADB',
  12: 'price_1NHyRzKBiRmOhngHZriSEWRW',
};

export const STRIPE_ONE_TIME_PRICES_SANDBOX = {
  1: 'price_1NHzFIKBiRmOhngHurzowZSS',
  6: 'price_1NHzFRKBiRmOhngH8XZvDasA',
  12: 'price_1NHzG1KBiRmOhngHeofiaH7g',
};

export const STRIPE_ONE_TIME_PRICES_KOL_LEGACY = {
  1: 'price_1NHyI1KBiRmOhngHMpxX4Gsv',
  6: 'price_1NHyJAKBiRmOhngHBuAWA2CH',
  12: 'price_1NHyIUKBiRmOhngHAWi2vB4a',
};

export const STRIPE_ONE_TIME_PRICES_KOL_LEGACY_SANDBOX = {
  1: 'price_1NHzF9KBiRmOhngHGZi4ww6q',
  6: 'price_1NHzFXKBiRmOhngHt7gZ4dyO',
  12: 'price_1NHzG9KBiRmOhngHpsUUzISZ',
};

export const getPaypalClientId = (sandbox: boolean): string =>
  sandbox ? PRICE_CONFIG_SANDBOX.clientId : PRICE_CONFIG_DEFAULT.clientId;

export const getStripePriceId = (
  promo: V1Coupon | undefined,
  month: MonthOptions,
  sandbox: boolean
): string => {
  const priceConfig = sandbox ? PRICE_CONFIG_SANDBOX : PRICE_CONFIG_DEFAULT;
  const prices = promo ? PLAN_KOL_V1_PROMO : priceConfig;
  return month === 1
    ? prices.stripe_plan1m
    : month === 6
      ? prices.stripe_plan6m
      : prices.stripe_plan1y;
};

export const planRestriction = (
  stripeCoupon?: StripeCoupon
): StripeCoupon['plan_restrictions'] => {
  if (!stripeCoupon?.plan_restrictions) {
    return ['12m', '6m', '1m'];
  }
  return stripeCoupon.plan_restrictions;
};

export const isMonthRestricted = (
  month: 1 | 6 | 12,
  stripeCoupon?: StripeCoupon
): boolean => !planRestriction(stripeCoupon).includes(`${month}m`);

export const priceReady = (
  PRICE_PER_MONTH: number,
  DISCOUNT_FOR_12_MONTH: number,
  loadingPrices: boolean
): boolean => {
  return PRICE_PER_MONTH > 0 && DISCOUNT_FOR_12_MONTH < 1 && !loadingPrices;
};

const roundTo99Cents = (price: number) => {
  // we always make our prices end with 99 cents
  if (price - Math.floor(price) !== 0.99) {
    return Math.floor(price) + 0.99;
  }
  return price;
};

export const totalAmountRaw = (
  PRICE_PER_MONTH: number,
  DISCOUNT_FOR_12_MONTH: number,
  loadingPrices: boolean,
  month: MonthOptions,
  v1Coupon: V1Coupon | undefined,
  stripeCoupon: StripeCoupon | undefined
) => {
  if (!priceReady(PRICE_PER_MONTH, DISCOUNT_FOR_12_MONTH, loadingPrices))
    return -1;
  // check promo

  const originalPrice = roundTo99Cents(
    month === 1
      ? 799.99
      : month <= 10
        ? month * PRICE_PER_MONTH
        : month * PRICE_PER_MONTH * (1 - DISCOUNT_FOR_12_MONTH)
  );

  if (stripeCoupon) {
    if (stripeCoupon.amount_off) {
      return originalPrice - stripeCoupon.amount_off;
    }
    if (stripeCoupon.percent_off) {
      return (originalPrice * (100 - stripeCoupon.percent_off)) / 100;
    }
  }
  if (v1Coupon) {
    if (month === 12) return PLAN_KOL_V1_PROMO.price1y;
    if (month === 6) return PLAN_KOL_V1_PROMO.price6m;
    return PLAN_KOL_V1_PROMO.price1m;
  }
  // no promo
  return originalPrice;
};

export const totalAmount = (
  PRICE_PER_MONTH: number,
  DISCOUNT_FOR_12_MONTH: number,
  loadingPrices: boolean,
  month: MonthOptions,
  v1Coupon: V1Coupon | undefined,
  stripeCoupon: StripeCoupon | undefined
) => {
  return totalAmountRaw(
    PRICE_PER_MONTH,
    DISCOUNT_FOR_12_MONTH,
    loadingPrices,
    month,
    v1Coupon,
    stripeCoupon
  ).toFixed(2);
};

export const usePromo = () => {
  const dispatch = useDispatch();

  const {
    promo,
    stripeCoupon,
    code: requestedPromo,
    loading: loadingPromo,
  } = useSelector(state => state.transaction.promo);

  const clearPromo = useCallback(() => {
    dispatch(transactionActions.clearPromo);
  }, [dispatch]);

  return {
    promo,
    stripeCoupon,
    requestedPromo,
    loadingPromo,
    clearPromo,
  };
};

export const usePrices = () => {
  const dispatch = useDispatch();

  const {
    PRICE_PER_MONTH,
    DISCOUNT_FOR_12_MONTH,
    loading: loadingPrices,
  } = useSelector(state => state.transaction.prices);

  useEffect(() => {
    if (!PRICE_PER_MONTH || !DISCOUNT_FOR_12_MONTH) {
      dispatch(transactionActions.getPrices());
    }
  }, [DISCOUNT_FOR_12_MONTH, PRICE_PER_MONTH, dispatch]);

  return {
    PRICE_PER_MONTH,
    DISCOUNT_FOR_12_MONTH,
    loading: loadingPrices,
  };
};

export const getEffectivePromoCode = (
  promo: V1Coupon | undefined,
  stripeCoupon: StripeCoupon | undefined
) => {
  if (stripeCoupon) {
    return stripeCoupon.promo_code;
  }
  if (promo) {
    return promo.code;
  }
  return null;
};
